/**
 *
 *  Become a Member page
 *  Component
 *
 *  Created by Vladimir Shishlyannikov on 17/04/2023.
 *  Copyright (c) 2023-present iWoo Ltd. All rights reserved.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import Slider from "react-slick";
import { useHistory, useLocation } from "react-router-dom";
import Modal from "react-modal";
import Cookies from "js-cookie";
import { Title, Meta } from "react-meta-elements";

import { useAppState } from "../../state";
import { useSignUpState } from "../../state/SignUpState";
import { useRegion } from "../../context/RegionProvider";

// Components
import Header from "./Header/Header";
import Footer from "./Footer/Footer";
import SignUpForm from "./SignUpForm/SignUpForm";
import CodeVerification from "../SignUp/CodeVerification/CodeVerification";
import Registration from "../SignUp/Registration/Registration";
import PasswordConfirmForm from "../SignUp/PasswordConfirmForm/PasswordConfirmForm";
import SetEmail from "../SignUp/SetEmail/SetEmail";
import SetPhoneNumber from "../SignUp/SetPhoneNumber/SetPhoneNumber";
import ScrollToTopOnMount from "../ScrollToTopOnMount/ScrollToTopOnMount";
import TrainerRating from "../TrainerRating/TrainerRating";
import Button from "../Button/Button";
import TBKTooltip from "../TruBeTooltip/TruBeTooltip";
import { EventCard } from "../LifestyleLP/LifestyleLP";
import UpcomingExperiencesSlider from "../LifestyleLP/UpcomingExperiencesSlider/UpcomingExperiencesSlider";
import AppBanner from "../AppBanner/AppBanner";
import AppBannerUK from "../UKComponents/AppBanner/AppBanner";

// Hooks
import useTheme from "../ThemeProvider/useTheme/useTheme";
import { useGetEvents } from "../../hooks/useEvents/useEvents";

// Icons
import CheckNiceIcon from "../../icons/CheckNiceIcon";
import BlockerIcon from "../../icons/BlockerIcon";
import CopyIcon from "../../icons/CopyIcon";

// Utils
import copy from "../../utils/copy";
import { clientWidth, makeCancelable, generatePassword } from "../../utils";
import { modalStyles } from "../../constants";

// Images
import Arrow from "./Arrow";

import becomeAMemberBig from "../../images/become-a-member-big.jpg";
import becomeAMemberBig2x from "../../images/become-a-member-big@2x.jpg";
import becomeAMemberHeroMobile from "../../images/become-a-member-hero-mobile.jpg";
import becomeAMemberHeroMobile2x from "../../images/become-a-member-hero-mobile@2x.jpg";

import HeartIcon from "../../icons/HeartIcon";
import HighQualityIcon from "../../icons/HighQualityIcon";
import OneOnOneSessionPriceIcon from "../../icons/OneOnOneSessionPriceIcon";
import VarietyOfDisciplinesIcon from "../../icons/VarietyOfDisciplinesIcon";

import allisonCoach from "./allison-coach.png";
import allisonCoach2x from "./allison-coach@2x.png";
import dillonCoach from "./dillon-coach.png";
import dillonCoach2x from "./dillon-coach@2x.png";
import koniCoach from "./koni-coach.png";
import koniCoach2x from "./koni-coach@2x.png";
import amberCoach from "./amber-coach.png";
import amberCoach2x from "./amber-coach@2x.png";

import paulaCoach from "./paula-coach.png";
import paulaCoach2x from "./paula-coach@2x.png";
import hannahCoach from "./hannah-coach.png";
import hannahCoach2x from "./hannah-coach@2x.png";
import christianCoach from "./christian-coach.png";
import christianCoach2x from "./christian-coach@2x.png";
import oliviaCoach from "./olivia-coach.png";
import oliviaCoach2x from "./olivia-coach@2x.png";

const AUTOHIDE_TIME = 3000;

const sliderResponsiveSettings = [
  {
    breakpoint: 640,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 960,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 1400,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 1600,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
  {
    breakpoint: 5000,
    settings: {
      slidesToShow: 1,
      slidesToScroll: 1,
      centerMode: true,
      centerPadding: "0",
    },
  },
];

const coachCardsUS = [
  {
    trainerName: "Allison",
    trainerPictureUrl: allisonCoach,
    trainerPictureSrcSet: `${allisonCoach2x} 2x`,
    productName: "Yoga",
    headline: "5+ Years of Experience Certifications: CPT",
  },
  {
    trainerName: "Koni",
    trainerPictureUrl: koniCoach,
    trainerPictureSrcSet: `${koniCoach2x} 2x`,
    productName: "Personal Training",
    headline: "7+ Years of Experience Certifications: Strength & Conditioning",
  },
  {
    trainerName: "Dillon",
    trainerPictureUrl: dillonCoach,
    trainerPictureSrcSet: `${dillonCoach2x} 2x`,
    productName: "Boxing",
    headline: "10+ Years of Experience Certifications: CPT",
  },
  {
    trainerName: "Amber",
    trainerPictureUrl: amberCoach,
    trainerPictureSrcSet: `${amberCoach2x} 2x`,
    productName: "Barre/Ballet Fit",
    headline: "9+ Years of Experience Certifications: Barre",
  },
];

const coachCardsUK = [
  {
    trainerName: "Paula",
    trainerPictureUrl: paulaCoach,
    trainerPictureSrcSet: `${paulaCoach2x} 2x`,
    productName: "Yoga",
    headline: "6+ Years of Experience Vinyasa Flow Yoga Teacher",
  },
  {
    trainerName: "Christian",
    trainerPictureUrl: christianCoach,
    trainerPictureSrcSet: `${christianCoach2x} 2x`,
    productName: "Personal Training",
    headline: "3+ Years of Experience Qualified Personal Trainer - Level 3",
  },
  {
    trainerName: "Hannah",
    trainerPictureUrl: hannahCoach,
    trainerPictureSrcSet: `${hannahCoach2x} 2x`,
    productName: "Pilates",
    headline: "Pilates 9+ Years of Experience Level 3 Pilates Teacher",
  },
  {
    trainerName: "Olivia",
    trainerPictureUrl: oliviaCoach,
    trainerPictureSrcSet: `${oliviaCoach2x} 2x`,
    productName: "Stretching",
    headline: "3+ Years of Experience Qualified Personal Trainer - Level 3",
  },
];

const TrainerCard = ({
  trainerPictureUrl,
  trainerPictureSrcSet,
  trainerName,
  headline,
  productName,
}) => {
  return (
    <div
      className="tbk-overflow-hidden tbk-rounded-2xl tbk-bg-basic-white tbk-shadow-trube"
      // onClick={handleCardClick}
    >
      <div className="tbk-relative tbk-flex">
        <div className="tbk-h-36 tbk-shrink-0 tbk-grow-0 tbk-basis-36">
          <img
            className="tb-profile-picture-img img-responsive tbk-h-full tbk-object-cover"
            itemProp="image"
            src={trainerPictureUrl}
            srcSet={trainerPictureSrcSet}
            alt="Coach profile"
          />
        </div>
        <div
          className="tbk-w-full tbk-p-2"
          style={{
            minWidth: 0,
          }}
        >
          <h4
            itemProp="name"
            className={
              "tbk-text-title-bold tbk-flex tbk-items-center tbk-text-primary"
            }
          >
            {trainerName}
            <TrainerRating
              className="tbk-ml-0.5 tbk-flex tbk-items-center"
              averageRatingClassName="tbk-text-coral tbk-text-secondary"
              partner={{
                partnerRatingRecap: {
                  average: 5,
                  numberOfRatings: 0,
                },
              }}
            />
          </h4>
          <div className="tbk-flex tbk-items-end">
            <div className="flex-grow-1">
              <div className="tbk-text-small tbk-max-h-[72px] tbk-overflow-hidden tbk-text-ellipsis tbk-text-primary">
                {productName}
                <br />
                {headline}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const ProgressIndicator = ({ level }) => {
  return (
    <div className="tbk-mb-5 tbk-flex tbk-items-center tbk-justify-center tbk-pb-1">
      {level > 1 ? (
        <div className="tbk-mr-1 tbk-inline-flex tbk-rounded-full tbk-bg-green tbk-text-basic-white">
          <CheckNiceIcon className="tbk-text-basic-white" />
        </div>
      ) : (
        <div className="tbk-mr-1 tbk-inline-flex tbk-h-3 tbk-w-3 tbk-items-center tbk-rounded-full tbk-bg-blue tbk-text-center tbk-text-basic-white">
          <span className="tbk-text-small-bold tbk-w-full tbk-text-basic-white">
            1
          </span>
        </div>
      )}

      {/*<CheckOIcon />*/}
      {/*<CheckNiceIcon />*/}
      <Arrow className="tbk-mr-1" />
      {level > 2 ? (
        <div className="tbk-mr-1 tbk-inline-flex tbk-rounded-full tbk-bg-green tbk-text-basic-white">
          <CheckNiceIcon className="tbk-text-basic-white" />
        </div>
      ) : (
        <div
          className={`tbk-mr-1 tbk-inline-flex tbk-h-3 tbk-w-3 tbk-items-center tbk-rounded-full tbk-border tbk-border-solid tbk-border-blue tbk-text-center ${
            level > 1 ? "tbk-bg-blue" : "tbk-bg-basic-white"
          }`}
        >
          <span
            className={`tbk-text-small-bold tbk-w-full ${
              level > 1 ? "tbk-text-basic-white" : "tbk-text-blue"
            }`}
          >
            2
          </span>
        </div>
      )}
      <Arrow className="tbk-mr-1" />
      {level > 3 ? (
        <div className="tbk-mr-1 tbk-inline-flex tbk-rounded-full tbk-bg-green tbk-text-basic-white">
          <CheckNiceIcon className="tbk-text-basic-white" />
        </div>
      ) : (
        <div
          className={`tbk-mr-1 tbk-inline-flex tbk-h-3 tbk-w-3 tbk-items-center tbk-rounded-full tbk-border tbk-border-solid tbk-border-blue tbk-text-center ${
            level > 2 ? "tbk-bg-blue" : "tbk-bg-basic-white"
          }`}
        >
          <span
            className={`tbk-text-small-bold tbk-w-full ${
              level > 2 ? "tbk-text-basic-white" : "tbk-text-blue"
            }`}
          >
            3
          </span>
        </div>
      )}
    </div>
  );
};

const isLoginPath = (pathname) => {
  return pathname.includes("/login") || pathname.includes("/become-a-member");
};

export default function BecomeMemberPage() {
  const history = useHistory();
  const location = useLocation();

  const { theme } = useTheme();
  const { region, city } = useRegion();

  const {
    user,
    referralCode,
    migrationCode,
    registrationCode,
    applyReferral,
    setReferralCode,
    setMigrationCode,
    signUp,
    signUpWithApple,
    signUpWithGoogle,
    signUpWithFacebook,
    checkEmail,
    verifyPhoneNumber,
    verifyPhoneNumberOrEmailForSimpleLogin,
    sendEmailCode,
    clearUpcomingEventsLoaded,
  } = useAppState();

  const {
    authState,
    isAppleSignIn,
    identityToken,
    authorizationCode,
    accessToken, // For Facebook login
    authVendor,
    userPhoneCode: phoneCode,
    userPhoneNumber: phoneNumber,
    userPassword,
    userFirstName: firstName,
    userFamilyName: familyName,
    userEmail,
    setUserPhoneCode,
    setUserPhoneNumber,
    setAuthVendor,
    setHasAuthError,
  } = useSignUpState();

  const { events: upcomingEvents, isPending: isUpcomingEventsLoading } =
    useGetEvents({
      customerId: user?.id,
      region: user ? user.region : region,
      city: user ? user.city : city,
      status: ["UPCOMING", "STARTED", "UPCOMING_FULL"],
      size: 8,
      sort: "start,asc",
      enabled: true, // Всегда включаем для этой страницы
    });

  const [_authState, setAuthState] = useState("");
  const [returnTo, setReturnTo] = useState("");
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);
  const [infoModalTitle, setInfoModalTitle] = useState("");
  const [infoModalMsg, setInfoModalMsg] = useState("");
  const [originPage, setOriginPage] = useState(null);
  const [isCopyTooltipShow, setIsCopyTooltipShow] = useState(false);
  const [ctaText, setCtaText] = useState("Finish registration");

  const heroRef = React.useRef(null);
  const infoModalResolveRef = useRef(null);
  const copyButtonRef = React.useRef(null);
  const hideCancelablePromiseRef = React.useRef(null);

  useEffect(
    () => {
      setHasAuthError(false);

      // return () => {
      //   clearUpcomingEventsLoaded();
      // };
    },
    [] // eslint-disable-line react-hooks/exhaustive-deps
  );

  useEffect(() => {
    setOriginPage(location.state && location.state.from);
  }, [location.state]);

  const membershipJoinCode = "TBHOL30"; //process.env.REACT_APP_MEMBERSHIP_JOIN_CODE; //"TBLIFE";

  // TODO write unit tests!
  const fromAboutPage =
    originPage &&
    (typeof originPage === "string"
      ? originPage.includes("about")
      : originPage &&
        originPage.pathname &&
        originPage.pathname.includes("about"));

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const returnTo = urlParams.get("return_to");

    setReturnTo(returnTo);

    // const urlParams = new URLSearchParams(location.search);
    // const authStateQueryString = urlParams.get("authState");
    //
    // if (authStateQueryString) {
    //   urlParams.delete("authState");
    // } else if (authState === "login") {
    //   history.push({
    //     pathname: "/login",
    //     state: location.state,
    //     search: urlParams.toString(),
    //   });
    // }
  }, []);

  const openInfoModal = useCallback((msg, title = "Sorry") => {
    return new Promise((resolve, reject) => {
      infoModalResolveRef.current = resolve;

      setIsInfoModalOpen(true);
      setInfoModalTitle(title);
      setInfoModalMsg(msg);
    });
  }, []);

  const closeInfoModal = useCallback(() => {
    infoModalResolveRef.current();
    infoModalResolveRef.current = null;

    setIsInfoModalOpen(false);
  }, []);

  const redirectToPreviousUrl = useCallback(
    (userInfo) => {
      const urlParams = new URLSearchParams(window.location.search);
      const returnTo = urlParams.get("return_to");

      if (returnTo) {
        urlParams.delete("return_to");
        urlParams.delete("authState");

        const returnUrl = new URL(
          decodeURIComponent(returnTo),
          window.location.origin
        );
        const pathname = returnUrl.pathname;
        const returnSearch = returnUrl.search;

        const search = returnSearch
          ? `${returnSearch}${
              urlParams.size > 0 ? `&${urlParams.toString()}` : ""
            }`
          : `${urlParams.size > 0 ? `?${urlParams.toString()}` : ""}`;

        if (userInfo && !userInfo.questionnaireCompleted) {
          history.replace({
            pathname: "/questionnaire",
            state: location.state,
            search: `?return_to=${encodeURIComponent(
              `${pathname}${search || ""}`
            )}`,
          });
        } else {
          history.replace({
            pathname,
            state: location.state,
            search: search
              ? `${search}${
                  urlParams.size > 0 ? `&${urlParams.toString()}` : ""
                }`
              : urlParams.size > 0
              ? `?${urlParams.toString()}`
              : "",
          });
        }
      } else {
        const stateFrom = location && location.state && location.state.from;

        let returnToUrl =
          typeof stateFrom === "object"
            ? `${stateFrom.pathname}${stateFrom.search}`
            : stateFrom;

        if (returnToUrl && returnToUrl.includes("/login")) {
          returnToUrl = "";
        }

        if (userInfo && !userInfo.questionnaireCompleted) {
          if (returnToUrl) {
            history.replace({
              pathname: "/questionnaire",
              search: `?return_to=${returnToUrl}`,
            });
          } else {
            history.replace("/questionnaire");
          }
        } else {
          urlParams.delete("authState");
          history.replace(
            returnToUrl || {
              pathname: "/lifestyle",
              search: urlParams.toString(),
            }
          );
        }
      }
    },
    [history, location, returnTo]
  );

  const handleCopyToClipboard = useCallback(
    (e) => {
      if (copy(membershipJoinCode)) {
        showCopyTooltip();
      }

      // Analytics.trackEvent(SHARE_CATEGORY, '', target.tagName.toLocaleLowerCase() === 'button' ? EVENT_SHARE_LINK : EVENT_SHARE_CLIPBOARD);
    },
    [membershipJoinCode] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const showCopyTooltip = useCallback(() => {
    if (hideCancelablePromiseRef.current) {
      return;
    }

    // Auto hide afer 4 sec
    var hidePromise = new Promise((resolve, reject) => {
      setIsCopyTooltipShow(true);

      setTimeout(() => {
        resolve();
      }, AUTOHIDE_TIME);
    });

    hideCancelablePromiseRef.current = makeCancelable(hidePromise);

    return hideCancelablePromiseRef.current.promise.then(() => {
      hideCancelablePromiseRef.current = null;
      hideCopyTooltip();
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const hideCopyTooltip = useCallback(() => {
    if (hideCancelablePromiseRef.current) {
      hideCancelablePromiseRef.current.cancel();
      hideCancelablePromiseRef.current = null;
    }

    setIsCopyTooltipShow(false);
  }, []);

  const onSignUp = useCallback(
    (userInfo) => {
      if (!userInfo) {
        return;
      }

      if (referralCode && false) {
        applyReferral(userInfo.id, referralCode)
          .then((res) => {
            // Referral code is valid
            // return openInfoModal(
            //   `You received ${res.data.symbol}${res.data.receiverReward} referral credits.`,
            //   "Referral Bonus Applied"
            // );
          })
          .catch((res) => {
            return openInfoModal(res.message, "Link Error");
          })
          .then(() => {
            redirectToPreviousUrl(userInfo);
          })
          .finally(() => {
            // Regardless the referral code is valid or not we don't need it anymore
            // (so it won't be applied on re-login for example)
            setReferralCode("");

            Cookies.remove("referral");
          });
      } else {
        redirectToPreviousUrl(userInfo);
      }
    },
    [history, location, referralCode]
  );

  const onSignIn = (userInfo) => {
    if (!userInfo) {
      return;
    }

    redirectToPreviousUrl(userInfo);
  };

  const redirectToLogin = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    urlParams.set("authState", "loginWithEmail");

    history.push({
      pathname: "/login",
      state: location.state,
      search: urlParams.toString(),
    });
  }, [location]);

  const handleSignIn = (userInfo, returnTo, applelogin) => {
    let returnToUrl;
    let stateFrom;

    if (applelogin) {
      returnToUrl = window.localStorage.getItem("apple-redirect-return-to");
      stateFrom = window.localStorage.getItem("apple-redirect-state-from");

      window.localStorage.removeItem("apple-redirect-return-to");
      window.localStorage.removeItem("apple-redirect-state-from");
    } else {
      stateFrom = location && location.state && location.state.from;

      // FIXME make more robust, quick fix for now
      if (stateFrom && isLoginPath(stateFrom)) {
        stateFrom = undefined;
      }
    }

    returnToUrl = returnToUrl || returnTo;

    if (typeof returnToUrl !== "string") {
      returnToUrl = returnToUrl && returnToUrl.pathname;
    }

    // FIXME quick fix to prevent to return to the login page again
    if (returnToUrl && !isLoginPath(returnToUrl)) {
      const query = new URLSearchParams(returnToUrl.split("?")[1]);

      // if (isLoginPath(returnToUrl)) {
      //   returnToUrl = "/partners";
      // }

      if (returnToUrl.includes("/partners")) {
        query.append("apple_login", "yes");
      }

      if (
        userInfo &&
        userInfo.customer &&
        !userInfo.customer.questionnaireCompleted
      ) {
        history.push({
          pathname: "/questionnaire",
          state: { from: stateFrom },
          search: `?return_to=${encodeURIComponent(returnToUrl)}`, //query.toString()`,
        });
      } else {
        history.replace({
          pathname: returnToUrl.split("?")[0],
          state: { from: stateFrom },
          search: query.toString(),
        });
      }
      //history.replace(returnToUrl);
    } else {
      // if (!applelogin) {
      //   stateFrom = location && location.state && location.state.from;
      //   // } else if (stateFrom && stateFrom === "/lifestyle") {
      //   // setPromoCode("TBHOL30");
      // }

      // // FIXME make more robust, quick fix for now
      // if (stateFrom && isLoginPath(stateFrom)) {
      //   stateFrom = undefined;
      // }

      // On login `state.from` is populated, so we preserve search
      // params that way.
      if (
        userInfo &&
        userInfo.customer &&
        !userInfo.customer.questionnaireCompleted
      ) {
        history.push("/questionnaire");
      } else {
        history.replace(
          stateFrom || {
            pathname: "/lifestyle",
            // search: window.location.search,
            search: applelogin ? "?apple_login=yes" : "",
          }
        );
      }
    }
    // window.location.href = ;
  };

  const handleSignUp = useCallback(
    ({ email, password, code, onNext }) => {
      // email = email || userEmail;
      password = password || userPassword;
      code = code || registrationCode;

      if (email && (!code || authState === "loginWithEmail")) {
        checkEmail(email)
          .then((res) => {
            if (res.status === "MIGRATION_CUSTOMER" && res.migrationData) {
              const { phoneCode, phoneNumber } = res.migrationData;

              setUserPhoneCode(phoneCode);
              setUserPhoneNumber(phoneNumber);

              setAuthState("codeVerificationEmailSignUp");
              setAuthVendor("");

              return sendEmailCode(email);
              // return verifyPhoneNumber({
              //   phoneCode,
              //   phoneNumber,
              //   authVendor: "",
              // });
            } else if (res.status === "EXISTING_CUSTOMER") {
              return verifyPhoneNumberOrEmailForSimpleLogin({ email });
            } else if (res.status === "NEW_CUSTOMER") {
              setAuthState("setPhoneNumber");
              setAuthVendor("");
            }

            return res;
          })
          .then((res) => {
            if (res.verified) {
              redirectToLogin();
            }

            if (res.status || res.verified) {
              onNext && onNext(res);
            }

            // if (res.verified) {
            //   setAuthState("passwordSet");
            // }
          })
          .catch(() => {
            setHasAuthError(true);
          });
      } // {
      else if (identityToken && authorizationCode) {
        signUpWithApple(
          firstName,
          familyName,
          generatePassword(12), //password,
          phoneCode,
          phoneNumber,
          code,
          email || userEmail,
          identityToken,
          authorizationCode,
          region
        )
          .then((res) => {
            // onNext && onNext(res);
            onSignUp(res);
          })
          .catch(() => {
            setHasAuthError(true);
          });
      } else if (identityToken && authVendor === "google") {
        signUpWithGoogle(
          firstName,
          familyName,
          phoneCode,
          phoneNumber,
          code,
          email || userEmail,
          identityToken,
          region
        )
          .then((res) => {
            // onNext && onNext(res);
            onSignUp(res);
          })
          .catch(() => {
            setHasAuthError(true);
          });
      } else if (accessToken && authVendor === "facebook") {
        signUpWithFacebook(
          firstName,
          familyName,
          phoneCode,
          phoneNumber,
          code,
          email || userEmail,
          accessToken,
          region
        )
          .then((res) => {
            // onNext && onNext(res);
            onSignUp(res);
          })
          .catch(() => {
            setHasAuthError(true);
          });
      } else {
        signUp(
          firstName,
          familyName,
          generatePassword(12), // password,
          phoneCode,
          phoneNumber,
          code,
          email || userEmail,
          region
        )
          .then((res) => {
            // onNext && onNext(res);
            onSignUp(res);
          })
          .catch(() => {
            setHasAuthError(true);
          });
      }
    },
    [
      authVendor,
      authState,
      registrationCode,
      identityToken,
      authorizationCode,
      phoneCode,
      phoneNumber,
      userPassword,
      firstName,
      familyName,
      userEmail,
      redirectToLogin,
      region,
    ]
  );

  const handleResetPasswordClick = useCallback(() => {
    const urlParams = new URLSearchParams(location.search);
    urlParams.set("authState", "passwordResetEmail");

    history.push({
      pathname: "/login",
      state: location.state,
      search: urlParams.toString(),
    });
  }, [history]);

  const handleNext = useCallback(
    (authVendor, res) => {
      if (res && res.customer) {
        onSignIn(res.customer);

        return;
      }

      // Migrated user verified phone number
      if (res.registrationCode && (res.migrationCode || migrationCode)) {
        handleSignUp({ code: res.registrationCode });

        return;
      }

      if (
        res.registrationCode ||
        (isAppleSignIn && authVendor !== "" && authVendor === undefined) ||
        authVendor === "google" ||
        authVendor === "facebook"
      ) {
        //? // ? userFirstName && userFamilyName ?  :
        setAuthState("setFullName");
      } else if (res.migrationCode) {
        // Migrated user verified email
        setAuthState("setPhoneNumber" /*"setPhoneNumberMigration"*/);

        // return;
        // } else {
        //   setAuthState("passwordSet");
      }
    },
    [isAppleSignIn, migrationCode, handleSignUp]
  );

  return (
    <div className="tbk-flex tbk-h-full tbk-flex-col tbk-items-stretch lg:tbk-grid lg:tbk-grid-rows-[64px_1fr_auto]">
      <ScrollToTopOnMount />
      {/*<Title>Personal Training in London | TruBe</Title>*/}
      <Meta
        name="description"
        content="TruBe connects you with the best fitness & wellbeing trainers, specialising in one-to-one sessions for Pilates, Yoga, Personal Training, HIIT, Running, and more"
      />
      <Header
        behaviour="none"
        isLoggedIn={!!(user && user.id)}
        heroRef={heroRef}
        // showLinks={!_authState}
        showLinks={false}
        // showMenu={!_authState}
        showMenu={false}
      />
      {!_authState ? (
        <div ref={heroRef} className="tbk-relative tbk-bg-grey-light !tbk-pt-0">
          <div className="tbk-absolute tbk-bottom-0 tbk-left-0 tbk-right-0 !tbk-hidden tbk-hidden tbk-h-74 tbk-w-full tbk-overflow-hidden tbk-bg-basic-trueblack md:tbk-h-full lg:tbk-bottom-auto lg:tbk-top-0 lg:tbk-block">
            <picture>
              <source
                media="(min-width: 768px)"
                srcSet={`${becomeAMemberBig} 1x, ${becomeAMemberBig2x} 2x,`}
              />
              <source
                srcSet={`${becomeAMemberHeroMobile} 1x, ${becomeAMemberHeroMobile2x} 2x,`}
              />
              <img
                className="tbk-h-full tbk-w-full tbk-grow tbk-object-cover"
                src={becomeAMemberBig2x}
                srcSet={`${becomeAMemberHeroMobile} 1x, ${becomeAMemberHeroMobile2x} 2x,`}
                alt="Hero"
              />
            </picture>
          </div>
          <div className="tbk-container tbk-z-1 tbk-mx-auto tbk-grow lg:tbk-px-2">
            <div className="md:tbk-pb-24_ tbk-relative tbk-flex tbk-h-full tbk-flex-col md:tbk-grid md:tbk-grid-cols-become-member-tablet-layout md:tbk-pb-6 lg:tbk-gap-6 lg:tbk-gap-8 xl:tbk-grid-cols-become-member-layout">
              <div className="md:tbk-mt-5">
                <div className="tbk-border-solid tbk-border-grey-inactive tbk-bg-basic-white tbk-p-3 md:tbk-rounded-2xl lg:tbk-border">
                  <SignUpForm
                    title={"Sign Up or Login"}
                    className=""
                    ctaText={"Continue"}
                    dividerText={"Or continue with"}
                    returnTo={
                      returnTo || (location.state && location.state.from)
                    }
                    onNext={(authVendor, params, isLogin, isEmailLogin) =>
                      authVendor === "google" || authVendor === "facebook"
                        ? setAuthState("setPhoneNumber")
                        : authVendor === "email"
                        ? (setAuthState("setEmail"), setCtaText("Continue"))
                        : setAuthState(
                            isLogin
                              ? "codeVerificationLogin"
                              : isEmailLogin
                              ? "codeVerificationEmail"
                              : "codeVerification"
                          )
                    }
                    onSignIn={handleSignIn}
                  />
                </div>
              </div>

              <div className="tbk-relative tbk-min-w-0 tbk-px-3 tbk-pb-3 tbk-pt-4 lg:tbk-h-auto lg:tbk-px-0 lg:tbk-pb-0 lg:tbk-pt-0">
                <div className="tbk-absolute tbk-bottom-0 tbk-left-0 tbk-right-0 tbk-top-0 !tbk-hidden tbk-h-full tbk-w-full tbk-overflow-hidden tbk-bg-grey-light lg:tbk-hidden">
                  <picture>
                    <source
                      media="(min-width: 768px)"
                      srcSet={`${becomeAMemberBig} 1x, ${becomeAMemberBig2x} 2x,`}
                    />
                    <source
                      srcSet={`${becomeAMemberHeroMobile} 1x, ${becomeAMemberHeroMobile2x} 2x,`}
                    />
                    <img
                      className="tbk-h-full tbk-w-full tbk-grow tbk-object-cover"
                      src={becomeAMemberBig2x}
                      srcSet={`${becomeAMemberHeroMobile} 1x, ${becomeAMemberHeroMobile2x} 2x,`}
                      alt="Hero"
                    />
                  </picture>
                </div>
                <div className="tbk-relative lg:tbk-pt-5">
                  <div className="tbk-pb-5">
                    {!fromAboutPage ? (
                      <h1 className="tbk-text-title-bold lg:tbk-text-h2-black tbk-mb-1 tbk-mt-1 tbk-text-primary lg:tbk-mb-2 lg:tbk-mt-0 lg:!tbk-leading-tight">
                        Join as a Member or Pay Per Class – <br />
                        <span className="tbk-text-coral">
                          The Choice is Yours
                        </span>
                        {/*<span className="tbk-text-coral">30% Off</span> Your
                        First Month's Membership: Use code:{" "}
                        <span className="tbk-text-coral">
                          {membershipJoinCode}
                        </span>*/}
                        {/*<div className="with-tooltip tbk-relative tbk-ml-1 tbk-inline-block">
                          <button
                            className="tbk-rounded-lg tbk-bg-basic-white tbk-p-0.5 tbk-shadow-trube"
                            ref={copyButtonRef}
                            onClick={handleCopyToClipboard}
                          >
                            <CopyIcon />
                          </button>
                          <TBKTooltip
                            element={copyButtonRef.current}
                            adjust
                            inside
                            placement="top"
                            isShow={isCopyTooltipShow}
                            onClick={hideCopyTooltip}
                          >
                            Copied!
                          </TBKTooltip>
                        </div>*/}
                      </h1>
                    ) : (
                      <h1 className="tbk-text-title-bold lg:tbk-text-h2-black tbk-mb-1 tbk-mt-1 tbk-text-primary lg:tbk-mb-2 lg:tbk-mt-0 lg:!tbk-leading-tight">
                        <span className="tbk-text-coral">Sign Up for Free</span>{" "}
                        and Get Access to Hundreds of Certified Coaches.
                      </h1>
                    )}
                    {!fromAboutPage ? (
                      <>
                        <p className="tbk-text-main tbk-mb-2 tbk-text-primary">
                          Discover exclusive wellness classes at luxury venues.
                          Engage in group classes like HIIT, Yoga, Pilates,
                          Meditation, and more. Enjoy pre & post-session brand
                          activations, complimentary snacks and beverages, and
                          curated swag bags!
                        </p>
                        {/*<p className="tbk-text-main tbk-mb-1 tbk-text-primary">
                          Three Classes a Month - {region === "US" ? "$" : "£"}
                          <span className="tbk-line-through tbk-opacity-50">
                            54
                          </span>{" "}
                          -> {region === "US" ? "$" : "£"}37 (First Month)
                        </p>*/}
                      </>
                    ) : (
                      <p className="tbk-text-main tbk-mb-4_ tbk-text-primary">
                        Currently available in{" "}
                        {region === "US" ? "Los Angeles, CA" : "London"} and
                        surrounding areas.
                        <br /> Launching in more cities soon...
                      </p>
                    )}
                  </div>

                  {!fromAboutPage ? (
                    <div>
                      <div className="tbk-relative tbk-mb-6 tbk-mt-5 tbk-text-center lg:tbk-mt-0">
                        <h3 className="tbk-text-h3-subtitle _lg:tbk-text-title-bold tbk-inline-block tbk-bg-grey-light tbk-px-2 tbk-text-center tbk-text-blue-grey">
                          How it works
                        </h3>
                        <hr className="tbk--mt-1.5 tbk-mb-0 tbk-border-blue-grey-light lg:tbk--mt-2" />
                      </div>
                      <div className="tbk-pb-7 md:tbk-grid md:tbk-gap-2 lg:tbk-grid-cols-3 lg:tbk-grid-cols-3 lg:tbk-pb-5">
                        <div className="tbk-relative tbk-mb-1 tbk-rounded-2xl tbk-bg-basic-white tbk-p-2 tbk-shadow-trube lg:tbk-text-center">
                          <div className="tbk-text-h3-subtitle tbk-bg-red tbk-absolute tbk-left-2 tbk-mb-2 tbk-inline-flex tbk-h-4 tbk-w-4 tbk-items-center tbk-justify-center tbk-rounded-full tbk-bg-blue-grey-light tbk-text-center !tbk-leading-relaxed tbk-text-primary lg:tbk-relative lg:tbk-left-0">
                            1
                          </div>
                          <div className="tbk-text-main tbk-ml-1 tbk-pl-5 tbk-text-primary  lg:tbk-ml-0 lg:tbk-pl-0">
                            Join the Lifestyle Collective & Access 3 Classes Per
                            Month
                          </div>
                        </div>

                        <div className="tbk-relative tbk-mb-1 tbk-rounded-2xl tbk-bg-basic-white tbk-p-2 tbk-shadow-trube lg:tbk-text-center">
                          <div className="tbk-text-h3-subtitle tbk-bg-red tbk-absolute tbk-left-2 tbk-mb-2 tbk-inline-flex tbk-h-4 tbk-w-4 tbk-items-center tbk-justify-center tbk-rounded-full tbk-bg-blue-grey-light tbk-text-center !tbk-leading-relaxed tbk-text-primary lg:tbk-relative lg:tbk-left-0">
                            2
                          </div>
                          <div className="tbk-text-main tbk-ml-1 tbk-pl-5 tbk-text-primary  lg:tbk-ml-0 lg:tbk-pl-0">
                            Choose Your Preferred Classes and RSVP
                          </div>
                        </div>

                        <div className="tbk-relative tbk-mb-1 tbk-rounded-2xl tbk-bg-basic-white tbk-p-2 tbk-shadow-trube lg:tbk-text-center">
                          <div className="tbk-text-h3-subtitle tbk-bg-red tbk-absolute tbk-left-2 tbk-mb-2 tbk-inline-flex tbk-h-4 tbk-w-4 tbk-items-center tbk-justify-center tbk-rounded-full tbk-bg-blue-grey-light tbk-text-center !tbk-leading-relaxed tbk-text-primary lg:tbk-relative lg:tbk-left-0">
                            3
                          </div>
                          <div className="tbk-text-main tbk-ml-1 tbk-pl-5 tbk-text-primary  lg:tbk-ml-0 lg:tbk-pl-0">
                            Receive Your Confirmation & Enjoy the Classes!
                          </div>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="tbk-mb-1.5 tbk-flex tbk-justify-between lg:tbk-mb-4 lg:tbk-justify-start">
                      <div className="tbk--ml-1.5 tbk--mr-1.5 tbk-text-center tbk-text-primary lg:tbk-ml-0 lg:tbk-mr-2">
                        <HeartIcon className="tbk-mb-1 tbk-inline-block" />
                        <div className="tbk-text-small tbk-w-32 !tbk-font-semibold md:tbk-px-1">
                          Sign Up & Browse
                          <br />
                          for Free
                        </div>
                      </div>
                      <div className="tbk--ml-1.5 tbk--mr-1.5 tbk-text-center tbk-text-primary lg:tbk-ml-0 lg:tbk-mr-2">
                        <HighQualityIcon className="tbk-mb-1 tbk-inline-block" />
                        <div className="tbk-text-small tbk-w-32 !tbk-font-semibold md:tbk-px-1">
                          Highly-Rated
                          <br />
                          Certified Coaches
                        </div>
                      </div>
                      <div className="tbk--ml-1.5 tbk--mr-1.5 tbk-hidden tbk-text-center tbk-text-primary lg:tbk-ml-0 lg:tbk-mr-2 xl:tbk-block ">
                        <VarietyOfDisciplinesIcon className="tbk-mb-1 tbk-inline-block" />
                        <div className="tbk-text-small tbk-w-32 !tbk-font-semibold md:tbk-px-1">
                          Variety
                          <br />
                          of Disciplines
                        </div>
                      </div>
                      <div className="tbk--ml-1.5 tbk--mr-1.5 tbk-text-center tbk-text-primary lg:tbk-ml-0 lg:tbk-mr-0">
                        <OneOnOneSessionPriceIcon className="tbk-mb-1 tbk-inline-block" />
                        <div className="tbk-text-small tbk-w-32 !tbk-font-semibold md:tbk-px-1">
                          1-on-1 Sessions
                          <br />
                          Start at $100
                        </div>
                      </div>
                    </div>
                  )}

                  {!fromAboutPage ? (
                    <div className="tbk-pb-3">
                      <div className="tbk-relative tbk-mb-3 tbk-text-center lg:tbk-mb-5">
                        <h3 className="tbk-text-h3-subtitle _lg:tbk-text-title-bold tbk-inline-block tbk-bg-grey-light tbk-px-2 tbk-text-center tbk-text-blue-grey">
                          Upcoming Classes
                        </h3>
                        <hr className="tbk--mt-1.5 tbk-mb-0 tbk-border-blue-grey-light lg:tbk--mt-2" />
                      </div>
                      <UpcomingExperiencesSlider
                        showArrows={false}
                        className="tbk-pb-1"
                        itemComponent={EventCard}
                        itemProps={{
                          imgClassName: "tbk-h-48",
                          // showRsvpCta: false,
                          // isUserLoggedIn: false,
                          // onRsvpCtaClick: ,
                        }}
                        sliderAutoplay={true}
                        futureEvents={upcomingEvents}
                        isFetching={isUpcomingEventsLoading}
                      />
                    </div>
                  ) : (
                    <div className="tbk-mb-4 xl:tbk-mb-2.5 xl:tbk-grid xl:tbk-grid-cols-2 xl:tbk-gap-3 xl:tbk-pt-0.5">
                      {clientWidth() < theme.responsive.breakpoints.xl ? (
                        <Slider
                          className="tbk--ml-2.5 tbk--mr-2.5"
                          dots={true}
                          dotsClass={
                            "slick-dots tbk-left-1/2 tbk--translate-x-1/2 tbk-pl-3"
                          }
                          arrows={false}
                          // autoplay
                          autoplaySpeed={5000}
                          infinite={true}
                          speed={500}
                          slidesToShow={1}
                          slidesToScroll={1}
                          responsive={sliderResponsiveSettings}
                        >
                          {region === "US"
                            ? coachCardsUS.map((info) => (
                                <div className="tbk-p-2.5">
                                  <TrainerCard {...info} />
                                </div>
                              ))
                            : coachCardsUK.map((info) => (
                                <div className="tbk-p-2.5">
                                  <TrainerCard {...info} />
                                </div>
                              ))}
                        </Slider>
                      ) : region === "US" ? (
                        coachCardsUS.map((info) => <TrainerCard {...info} />)
                      ) : (
                        coachCardsUK.map((info) => <TrainerCard {...info} />)
                      )}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      ) : (
        <div className="tbk-flex tbk-flex-col tbk-items-center tbk-justify-center tbk-bg-basic-white tbk-pb-7 lg:tbk-pb-8 lg:tbk-pt-7">
          <div className="tbk-container tbk-px-3 lg:tbk-px-8 lg:tbk-pb-5">
            <div className="tbk-relative tbk-mx-auto tbk-mt-4 md:tbk-mt-0 md:tbk-w-108">
              {_authState === "setPhoneNumber" ? (
                <>
                  <ProgressIndicator level={1} />
                  <SetPhoneNumber
                    onNext={() => setAuthState("codeVerification")}
                  />
                </>
              ) : _authState === "codeVerification" ||
                _authState === "codeVerificationEmail" ||
                _authState === "codeVerificationEmailSignUp" ||
                _authState === "loginWithEmail" ||
                _authState === "codeVerificationLogin" ? (
                <>
                  <ProgressIndicator
                    level={
                      isAppleSignIn ||
                      authVendor === "google" ||
                      authVendor === "facebook"
                        ? 2
                        : 1
                    }
                  />
                  <CodeVerification
                    isLogin={
                      _authState === "loginWithEmail" ||
                      _authState === "codeVerificationLogin" ||
                      _authState === "codeVerificationEmail"
                    }
                    isSignUp={_authState === "codeVerificationEmailSignUp"} // is it enough??
                    withEmail={_authState === "codeVerificationEmail"}
                    onNext={handleNext}
                    onResetPasswordClick={handleResetPasswordClick}
                    onCreateNewAccountClick={() => setAuthState("")}
                    showIcon={false}
                  />
                </>
              ) : _authState === "passwordSet" ? (
                <>
                  <ProgressIndicator level={2} />
                  <PasswordConfirmForm
                    onNext={() => setAuthState("setFullName")}
                  />
                </>
              ) : _authState === "setFullName" ? (
                <>
                  <ProgressIndicator level={3} />
                  <Registration onNext={() => setAuthState("setEmail")} />
                </>
              ) : _authState === "setEmail" ? (
                <>
                  <ProgressIndicator level={ctaText === "Continue" ? 1 : 4} />
                  <SetEmail ctaText={ctaText} onNext={handleSignUp} />
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}
      {!_authState ? region == "US" ? <AppBanner /> : <AppBannerUK /> : null}
      <Footer />

      <Modal
        appElement={/*__CLIENT__ && */ document.getElementById("#app")}
        isOpen={isInfoModalOpen}
        // onAfterOpen={afterOpenInfoModal}
        onRequestClose={closeInfoModal}
        style={
          clientWidth() < theme.responsive.breakpoints.sm
            ? modalStyles.infoMobile
            : modalStyles.info
        }
        contentLabel="Info Modal"
      >
        <div className="modal-info tbk-flex tbk-flex-col tbk-items-center">
          <h3 className="tbk-text-title-bold tbk-mb-1 tbk-mt-0 tbk-text-center tbk-text-grey-main">
            {infoModalTitle}
          </h3>
          <p className="tbk-text-main tbk-mb-4 tbk-max-w-full tbk-text-center tbk-text-grey-main">
            {infoModalMsg}
          </p>
          {infoModalTitle === "Referral Bonus Applied" ? (
            <CheckNiceIcon size={40} className="tbk-mb-4 tbk-text-green" />
          ) : (
            <BlockerIcon size={40} className="tbk-mb-4 tbk-text-green" />
          )}
          <Button className="tbk-w-full" onClick={closeInfoModal}>
            Ok
          </Button>
        </div>
      </Modal>
    </div>
  );
}
